import { useMemo, useState } from 'react';

import { definitions } from '@luxuryescapes/contract-svc-promo';

import { useGetPromoRequests } from '~/hooks/apis/promo/useGetPromoRequests';

import { DiscountItemInfo, PromoRequest, PromoRequestFilters, PromoResponseJSON } from '~/services/PromoService';

import { getCurrencyCodeByRegionCode } from '~/utils/getCurrencyCodeByRegionCode';

type UsePromoRequestsProps = {
  initPage?: number;
  initLimit?: number;
  initCodeName?: string;
  initVoucherName?: string;
  initUserId?: string;
  initErrorCode?: string;
  initRequestSource?: string;
  brand: string;
};

export type InputPromoOrder = definitions['Discount Request']['order'];

export type VoucherApplied = {
  voucherId?: string;
  voucherName?: string;
  voucherTitle?: string;
  voucherCurrency?: string;
  amount?: number;
  remainingAmountAfter?: number;
  expiresAt?: string;
  voucherUserId?: string;
  voucherDescription?: string;
  /** API rows use full toggle objects; promo log flat rows may store toggle keys only. */
  voucherToggles?: Array<definitions['VoucherToggles'] | definitions['VoucherToggles']['key']>;
};

export type PromoRequestItem = definitions['Discount Request Item'] & {
  codeName?: string;
  region?: string;
  deviceType?: string;
  requestSource?: string;
  requestAt?: string;
  currencyCode?: string;
  uniqueKey?: string;
  userId?: string;
  orderId?: string;
  isDeleted?: boolean;
  /** A number is the real HTTP status. `null` is set by usage-log parsing when the log has no status (inferred in UI). `undefined` is omitted on API-sourced request rows. */
  statusCode?: number | null;
  errorCode?: string;
  errorMsg?: string;
  clientOrderVersion?: number;
  fullRequest: InputPromoOrder;
  discountItem?: DiscountItemInfo | null;
  mx?: string;
  fareType?: string;
  transactionKey?: string;
  vouchersApplied?: Array<VoucherApplied>;
  voucherTotal?: number;
  /** Set from API / contract `Discount Request Item` once published */
  isFnFBooking?: boolean;
  /** From `log_items_2` / `Promo log flat item` when showing promo usage log lines */
  statusMessage?: string | null;
  warningCode?: string | null;
  countryCode?: string | null;
  logLineDetails?: string | null;
  logMetadata?: definitions['Promo request line error metadata'];
  linePrice?: number | null;
  /** `productType` string from promo log flat item (presentation key), not the same as category-derived product label */
  logProductType?: string | null;
};

export type PromoRequestLoyaltyItemExtended = definitions['LoyaltyCalculationRequestItem'] & {
  codeName?: string;
  region?: string;
  deviceType?: string;
  orderId?: string;
  promoKey?: string;
  uniqueKey?: string;
  loyaltyBonusFilterMethod?: string;
  resultCode?: string;
};

type ResponsePromoItem = {
  itemId: string;
  categoryBK?: string;
  subCategoryBK?: string;
  provider?: string;
  vouchersApplied?: Array<VoucherApplied>;
};

function extractResponsePromoItems(pr: PromoRequest): Array<ResponsePromoItem> {
  if (
    pr?.responseJSON?.result &&
    typeof pr.responseJSON.result === 'object' &&
    'promo' in pr.responseJSON.result &&
    typeof (pr.responseJSON.result as Record<string, unknown>).promo === 'object'
  ) {
    const promo = (pr.responseJSON.result as Record<string, unknown>).promo;
    if (
      promo &&
      typeof promo === 'object' &&
      'items' in promo &&
      Array.isArray((promo as Record<string, unknown>).items)
    ) {
      return (promo as Record<string, unknown>).items as Array<ResponsePromoItem>;
    }
  }
  return [];
}

function extractVoucherTotal(pr: PromoRequest): number | undefined {
  if (
    pr?.responseJSON?.result &&
    typeof pr.responseJSON.result === 'object' &&
    'promo' in pr.responseJSON.result &&
    typeof (pr.responseJSON.result as Record<string, unknown>).promo === 'object'
  ) {
    const promo = (pr.responseJSON.result as Record<string, unknown>).promo;
    if (
      promo &&
      typeof promo === 'object' &&
      'voucherTotal' in promo &&
      typeof (promo as Record<string, unknown>).voucherTotal === 'number'
    ) {
      return (promo as Record<string, unknown>).voucherTotal as number;
    }
  }
  return undefined;
}

function transformPromoRequests(
  rawRequests: Array<definitions['PromoCodeRequest']>,
): Array<PromoRequest & { uniqueKey: string }> {
  return rawRequests.map((pr, i) => ({
    uniqueKey: `${pr.codeName}-${pr.createdAt}-${i}`,
    ...pr,
  }));
}

function transformPromoRequestItems(rawRequests: Array<definitions['PromoCodeRequest']>): Array<PromoRequestItem> {
  return rawRequests.flatMap((pr, requestIndex) => {
    const codeName = pr?.codeName;
    if (typeof pr?.bodyJSON === 'string') {
      try {
        pr.bodyJSON = JSON.parse(pr?.bodyJSON);
      } catch (e) {
        console.error(`(newPromoRequestItems) nonbreaking bodyJSON parse issue. JSON.parse error ${pr.codeName}`, e);
      }
    }
    const region = pr?.bodyJSON?.order?.region;
    const currencyCode = region != null ? getCurrencyCodeByRegionCode(region) ?? undefined : undefined;
    const responseItems = extractResponsePromoItems(pr as PromoRequest);
    const voucherTotal = extractVoucherTotal(pr as PromoRequest);

    return (
      pr?.bodyJSON?.order?.items.map((item, itemIndex): PromoRequestItem => {
        const matchingResponseItem = responseItems?.find((ri) => ri.itemId === item.itemId);
        return {
          ...item,
          codeName,
          region,
          deviceType: pr?.bodyJSON?.order?.deviceType,
          userId: pr?.bodyJSON?.userId,
          requestAt: pr?.createdAt,
          currencyCode,
          uniqueKey: `${pr.codeName}-${pr.createdAt}-${requestIndex}-${itemIndex}`,
          requestSource: pr?.bodyJSON?.requestSource,
          statusCode: pr?.statusCode,
          errorCode: pr?.errorCode,
          errorMsg: pr?.errorMsg,
          fullRequest: pr?.bodyJSON?.order,
          vouchersApplied: matchingResponseItem?.vouchersApplied,
          voucherTotal,
        };
      }) ?? []
    );
  });
}

function transformPromoRequestLoyaltyItems(
  rawRequests: Array<definitions['PromoCodeRequest']>,
): Array<PromoRequestLoyaltyItemExtended> {
  return rawRequests.flatMap((pr, requestIndex) => {
    if (!pr) {
      return [];
    }

    const loyaltyItems = pr?.bodyJSON?.order?.loyaltyItems;
    if (!loyaltyItems || !Array.isArray(loyaltyItems)) {
      return [];
    }

    return loyaltyItems
      .map((item, itemIndex): PromoRequestLoyaltyItemExtended | null => {
        if (item == null) {
          return null;
        }

        const response: PromoResponseJSON | undefined = (pr as PromoRequest)?.responseJSON?.result as PromoResponseJSON;
        const loyaltyPointsBonusResultV1 = response?.promo?.loyaltyPointsBonusResultV1;
        const resultItem = loyaltyPointsBonusResultV1?.loyaltyCalculationResponseItems?.find(
          (i) => i?.itemId === item?.itemId,
        );

        return {
          ...item,
          codeName: pr?.codeName ?? undefined,
          region: pr?.bodyJSON?.order?.region ?? undefined,
          deviceType: pr?.bodyJSON?.order?.deviceType ?? undefined,
          loyaltyProductType: item?.loyaltyProductType ?? undefined,
          promoKey: resultItem?.promoKey ?? undefined,
          loyaltyBonusFilterMethod: loyaltyPointsBonusResultV1?.loyaltyBonusFilterMethod ?? undefined,
          resultCode: loyaltyPointsBonusResultV1?.resultCode ?? 'NO-ITEM-FOUND-IN-RESULT',
          price: item?.price ?? undefined,
          itemId: item?.itemId ?? undefined,
          offerId: item?.offerId ?? undefined,
          packageId: item?.packageId ?? undefined,
          uniqueKey: `${pr?.codeName ?? 'unknown'}-${pr?.createdAt ?? Date.now()}-${requestIndex}-${itemIndex}`,
        };
      })
      .filter((item): item is PromoRequestLoyaltyItemExtended => item != null);
  });
}

export default function usePromoRequests({
  initPage,
  initLimit,
  initCodeName,
  initVoucherName,
  initErrorCode,
  initUserId,
  initRequestSource,
  brand,
}: UsePromoRequestsProps): {
  promoRequests: Array<PromoRequest>;
  promoRequestItems: Array<PromoRequestItem>;
  promoRequestLoyaltyItems: Array<PromoRequestLoyaltyItemExtended>;
  totalRows: number;
  isLoading: boolean;
  error: Error | null;
  promoRequestsFilter: PromoRequestFilters;
  setPromoRequestsFilter: React.Dispatch<React.SetStateAction<PromoRequestFilters>>;
  refetch: () => void;
} {
  const [promoRequestsFilter, setPromoRequestsFilter] = useState<PromoRequestFilters>({
    codeName: initCodeName,
    voucherName: initVoucherName,
    userId: initUserId,
    brand: brand as definitions['Promo Brands'],
    errorCode: initErrorCode,
    requestSource: initRequestSource,
    page: initPage ?? 0,
    limit: initLimit != null && initLimit > 0 ? initLimit : 10,
  });

  const { data, isLoading, error, refetch } = useGetPromoRequests({
    filters: promoRequestsFilter,
  });

  const rawRequests = useMemo(() => data?.result?.promoRequests ?? [], [data]);
  const totalRows = data ? Number(data.total) : 0;

  const promoRequests = useMemo(() => transformPromoRequests(rawRequests), [rawRequests]);

  const promoRequestItems = useMemo(() => transformPromoRequestItems(rawRequests), [rawRequests]);

  const promoRequestLoyaltyItems = useMemo(() => transformPromoRequestLoyaltyItems(rawRequests), [rawRequests]);

  return {
    promoRequests,
    promoRequestItems,
    promoRequestLoyaltyItems,
    totalRows,
    isLoading,
    error,
    promoRequestsFilter,
    setPromoRequestsFilter,
    refetch,
  };
}
